import { Link } from "react-router-dom";

/** Grey shapes in the layout of the page that is loading (PLAN.md item 110), so it looks ready at
 * once: post cards, rows with a round picture, chat bubbles, a title head or a profile head. */
export type LoadingShape = "block" | "feed" | "list" | "chat" | "title" | "profile";

const Bar = ({ w, h = 12 }: { w: string; h?: number }) => <span className="skel skel-line" style={{ width: w, height: h }} />;
const Row = ({ i }: { i: number }) => (
  <div className="skel-row">
    <span className="skel skel-circle" />
    <div className="skel-lines">
      <Bar w={`${55 + ((i * 17) % 30)}%`} />
      <Bar w={`${30 + ((i * 23) % 35)}%`} h={10} />
    </div>
  </div>
);
const Card = ({ i }: { i: number }) => (
  <div className="skel-card">
    <div className="skel-row">
      <span className="skel skel-circle" />
      <div className="skel-lines">
        <Bar w="40%" />
        <Bar w="22%" h={10} />
      </div>
    </div>
    <div className="skel-card-body">
      <span className="skel skel-poster" />
      <div className="skel-lines">
        <Bar w={`${60 + ((i * 13) % 25)}%`} h={16} />
        <Bar w="30%" />
        <Bar w="90%" h={10} />
        <Bar w="70%" h={10} />
      </div>
    </div>
  </div>
);

export function Loading({ shape = "block" }: { shape?: LoadingShape }) {
  return (
    <div className={`page state-loading skel-${shape}`} aria-busy="true">
      {shape === "block" && <div className="skeleton-hero" />}
      {shape === "feed" && [0, 1, 2].map((i) => <Card key={i} i={i} />)}
      {shape === "list" && (
        <>
          <Bar w="45%" h={28} />
          {[0, 1, 2, 3, 4, 5].map((i) => <Row key={i} i={i} />)}
        </>
      )}
      {shape === "chat" && (
        <>
          <div className="skel-row">
            <span className="skel skel-circle" />
            <Bar w="35%" h={18} />
          </div>
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className={`skel skel-bubble${i % 2 ? " mine" : ""}`} style={{ width: `${40 + ((i * 19) % 35)}%` }} />
          ))}
        </>
      )}
      {shape === "title" && (
        <>
          <span className="skel skel-backdrop" />
          <div className="skel-card-body">
            <span className="skel skel-poster" />
            <div className="skel-lines">
              <Bar w="70%" h={24} />
              <Bar w="35%" />
              <Bar w="50%" h={40} />
            </div>
          </div>
          <Bar w="95%" h={10} />
          <Bar w="88%" h={10} />
          <Bar w="60%" h={10} />
        </>
      )}
      {shape === "profile" && (
        <>
          <div className="skel-row skel-profile-head">
            <span className="skel skel-circle skel-big" />
            <div className="skel-lines">
              <Bar w="50%" h={22} />
              <Bar w="35%" />
            </div>
          </div>
          <div className="skel-stats">
            {[0, 1, 2].map((i) => (
              <span key={i} className="skel skel-stat" />
            ))}
          </div>
          {[0, 1, 2].map((i) => (
            <Row key={i} i={i} />
          ))}
        </>
      )}
      <span className="sr-only">Loading</span>
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="page state">
      <p className="form-error" role="alert">
        {message}
      </p>
      {onRetry && (
        <button type="button" className="btn btn-quiet" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function NotFoundState({ what = "page" }: { what?: string }) {
  return (
    <div className="page state">
      <h1 className="page-title">We couldn't find that {what}</h1>
      <p>
        <Link to="/search">Search for something else</Link>
      </p>
    </div>
  );
}
